import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ChevronLeft, Loader2, Clock, Pencil,
  Video, FileType, FileText, Image as ImageIcon, Trash2,
  Clapperboard, Upload, ChevronDown, MoreHorizontal, Copy,
  Play, Pause, RectangleHorizontal, RectangleVertical, Plus, GripVertical, X, Link2,
  CalendarClock, Volume2, VolumeX, Save,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Badge } from '@meno/ui/components/ui/badge'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from '@meno/ui/components/ui/dropdown-menu'
import { getPlaylist, updatePlaylist, deletePlaylist, publishPlaylist, BACKEND_URL } from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { formatDate } from '@meno/ui/lib/utils'
import PlaylistContentPicker, { type PickedItem } from '@/components/PlaylistContentPicker'
import { PublishDialog } from '@/components/PublishDialog'
import { withCompanions } from '@/lib/companions'
import { RowScheduleDialog } from '@/components/RowScheduleDialog'
import {
  isVisibleAt,
  DEFAULT_ROW_SCHEDULE as DEFAULT_SCHEDULE,
  type RowSchedule,
} from '@/lib/rowSchedule'
import { playlistThumbUrl } from '@/lib/playlistThumb'
import PdfThumbnail from '@/components/PdfThumbnail'
import DurationInput from '@/components/DurationInput'

type MediaFile = { id: number; original_name: string; file_type: string; url: string; duration?: number }
type LinkItem  = { id: number; name: string; interactive_link?: string }
type ScreenRef = { id: number; name: string; branch?: { id: number; name: string } | null }
type PlaylistData = {
  id: number; name: string; description?: string;
  media: MediaFile[]; media_durations: Record<number, number>;
  links: LinkItem[];  link_durations: Record<number, number>;
  media_order: Record<number, number>;
  link_order:  Record<number, number>;
  media_muted: Record<number, boolean>;
  // Per-item visibility windows (ISO-UTC bounds, either may be null). Absent
  // key = always on. Persisted on the playlist_media / playlist_links rows and
  // enforced on real screens by the device manifest.
  media_schedule: Record<number, { start: string | null; end: string | null }>;
  link_schedule:  Record<number, { start: string | null; end: string | null }>;
  total_duration: number; screens: ScreenRef[]; created_at: string;
  // Publish companions (audio source / stop screen / transition) — chosen in
  // the create dialog and co-published with the playlist by withCompanions.
  companion_audio_id?: number | null;
  companion_audio_playlist_id?: number | null;
  companion_stop_screen_id?: number | null;
  companion_transition?: string | null;
}

// Unified content row — media + links live in one ordered list keyed by
// `${kind}-${id}` so React can diff stably across reorders.
type ContentRow =
  | { key: string; kind: 'media'; pos: number; item: MediaFile; duration: number; isVideo: boolean; isPdf: boolean; isImage: boolean; muted: boolean }
  | { key: string; kind: 'link';  pos: number; item: LinkItem;  duration: number }

const THUMB_CACHE_PREFIX = 'meno_vthumb_'

function fmtDur(sec: number) {
  if (sec < 60) return `${sec}s`
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

function defaultDurationFor(row: ContentRow): number {
  if (row.kind === 'link') return row.duration > 0 ? row.duration : 10
  if (row.isVideo) return row.duration || row.item.duration || 10
  return row.duration > 0 ? row.duration : 10
}

// Signature of everything a save persists — used to detect buffered, unsaved
// edits (content membership/order/durations/mute, per-row schedules, name).
function playlistSig(pl: PlaylistData): string {
  return JSON.stringify({
    name: pl.name,
    m: pl.media.map(x => x.id),
    l: pl.links.map(x => x.id),
    mo: pl.media_order, lo: pl.link_order,
    md: pl.media_durations, ld: pl.link_durations,
    mm: pl.media_muted,
    ms: pl.media_schedule, ls: pl.link_schedule,
  })
}

export default function PlaylistDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  // Always ChevronLeft — a global CSS rule mirrors it under dir="rtl"; see
  // WizardNavButton.tsx for why swapping it here too double-flips it back.
  const ChevronIcon = ChevronLeft

  const [playlist, setPlaylist] = useState<PlaylistData | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  // Signature of the last-saved state. Edits buffer into `playlist` locally and
  // are only persisted when the operator hits Save / Publish; `dirty` compares
  // the live state to this so we can gate saving + warn before leaving.
  const [savedSig, setSavedSig] = useState('')
  const dirty = playlist != null && playlistSig(playlist) !== savedSig
  const { guardOpen, guardNavigate, confirmLeave, cancelLeave } = useUnsavedChangesGuard(dirty)
  const [deleteOpen, setDeleteOpen] = useState(false)
  // Publish / Schedule share one dialog (PublishDialog); the mode picks
  // whether the start/end window inputs are shown. null = closed.
  const [publishMode, setPublishMode] = useState<'publish' | 'schedule' | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape')
  const [thumbCache, setThumbCache] = useState<Record<number, string>>({})

  // Drag-and-drop reorder state. We key everything by ContentRow.key so a
  // mixed media+link list still sorts cleanly.
  const [dragKey,     setDragKey]     = useState<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  const [titleDraft, setTitleDraft] = useState('')
  const [editingTitle, setEditingTitle] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Preview playback — cycles through rows once. `previewIdx` is the row
  // index being shown; `previewing` is the on/off flag. We use a manual
  // timer so non-video items wait their full duration before advancing.
  const [previewing, setPreviewing] = useState(false)
  const [previewIdx, setPreviewIdx] = useState(0)
  const timerRef = useRef<number | null>(null)

  // Per-row visibility schedules are persisted on the backend (media_schedule
  // / link_schedule) and enforced on real screens. `now` ticks every 30s so
  // visibility transitions show up live without a manual refresh.
  const [scheduleRowKey, setScheduleRowKey] = useState<string | null>(null)
  const [now, setNow] = useState<Date>(() => new Date())

  useEffect(() => {
    const tick = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(tick)
  }, [])

  // Derive a row's schedule from the playlist's persisted window maps. A row
  // key is `m-<id>` (media) or `l-<id>` (link); an absent / empty window means
  // always-on.
  const getSchedule = (key: string): RowSchedule => {
    if (!playlist) return DEFAULT_SCHEDULE
    const rid = Number(key.slice(2))
    const map = key.startsWith('m-') ? playlist.media_schedule : playlist.link_schedule
    const w = map?.[rid]
    if (!w || (!w.start && !w.end)) return DEFAULT_SCHEDULE
    return { alwaysOn: false, startAt: w.start ?? undefined, endAt: w.end ?? undefined }
  }

  // Buffer a row's schedule locally. `{start:null,end:null}` clears it back to
  // always-on. Persisted on Save / Publish like every other edit.
  const updateSchedule = (key: string, next: RowSchedule) => {
    if (!playlist) return
    const rid = Number(key.slice(2))
    const window = next.alwaysOn
      ? { start: null, end: null }
      : { start: next.startAt ?? null, end: next.endAt ?? null }
    setPlaylist(key.startsWith('m-')
      ? { ...playlist, media_schedule: { ...playlist.media_schedule, [rid]: window } }
      : { ...playlist, link_schedule:  { ...playlist.link_schedule,  [rid]: window } })
  }

  // Flush the buffered edits to the backend in one PATCH. Returns the saved
  // playlist (or null on failure). Save / Publish are the only writers now.
  const saveChanges = async (): Promise<PlaylistData | null> => {
    if (!playlist) return null
    setSaving(true)
    try {
      const { data } = await updatePlaylist(playlist.id, {
        name: playlist.name,
        media_ids: playlist.media.map(m => m.id),
        link_ids: playlist.links.map(l => l.id),
        media_durations: playlist.media_durations,
        link_durations: playlist.link_durations,
        media_order: playlist.media_order,
        link_order: playlist.link_order,
        media_muted: playlist.media_muted,
        media_schedule: playlist.media_schedule,
        link_schedule: playlist.link_schedule,
      })
      setPlaylist(data)
      setSavedSig(playlistSig(data))
      return data
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
      return null
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (!id) return
    const load = async () => {
      try {
        const { data } = await getPlaylist(Number(id))
        setPlaylist(data)
        setSavedSig(playlistSig(data))
        const cache: Record<number, string> = {}
        data.media?.forEach((m: MediaFile) => {
          if (m.file_type.startsWith('video')) {
            const stored = localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`)
            if (stored) cache[m.id] = stored
          }
        })
        setThumbCache(cache)
      } catch {
        toast.error('Failed to load playlist')
        navigate('/management/media')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, navigate])

  // ── Unified content list ───────────────────────────────────────────────
  // Merge media + links into one array ordered by their stored position.
  // Items missing a position default to a high number so they go last.
  const rows: ContentRow[] = useMemo(() => {
    if (!playlist) return []
    const mediaRows: ContentRow[] = playlist.media.map(m => {
      const isImage = m.file_type.startsWith('image')
      const isVideo = m.file_type.startsWith('video')
      const isPdf   = m.file_type === 'application/pdf' || /\.pdf$/i.test(m.original_name)
      const explicit = playlist.media_durations[m.id]
      const duration = explicit ?? (isVideo ? (m.duration ?? 0) : 10)
      return {
        key: `m-${m.id}`,
        kind: 'media',
        pos: playlist.media_order[m.id] ?? 1e6,
        item: m,
        duration,
        isImage, isVideo, isPdf,
        muted: !!(playlist.media_muted && playlist.media_muted[m.id]),
      }
    })
    const linkRows: ContentRow[] = playlist.links.map(l => ({
      key: `l-${l.id}`,
      kind: 'link',
      pos: playlist.link_order[l.id] ?? 1e6,
      item: l,
      duration: playlist.link_durations[l.id] ?? 10,
    }))
    return [...mediaRows, ...linkRows].sort((a, b) => a.pos - b.pos)
  }, [playlist])

  // Cancel any pending preview tick when rows change or component unmounts.
  useEffect(() => () => {
    if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null }
  }, [])

  const handleDelete = async () => {
    if (!playlist) return
    try {
      await deletePlaylist(playlist.id)
      toast.success(t('common.playlistDeleted', 'Playlist deleted'))
      navigate('/management/media')
    } catch {
      toast.error(t('common.playlistDeleteFailed', 'Failed to delete playlist'))
    }
    setDeleteOpen(false)
  }

  // ── Local edit buffer ───────────────────────────────────────────────────
  // Every content edit (drag, add, remove, duration) rebuilds the ordered
  // maps into local state only. Nothing is persisted until Save / Publish, so
  // the playlist "doesn't change" until the operator commits it.
  const applyRows = (next: ContentRow[]) => {
    if (!playlist) return
    const media: MediaFile[] = []
    const links:  LinkItem[] = []
    const mediaDurations: Record<number, number> = {}
    const linkDurations:  Record<number, number> = {}
    const mediaOrder: Record<number, number> = {}
    const linkOrder:  Record<number, number> = {}
    const mediaMuted: Record<number, boolean> = {}
    next.forEach((row, i) => {
      if (row.kind === 'media') {
        media.push(row.item)
        mediaDurations[row.item.id] = defaultDurationFor(row)
        mediaOrder[row.item.id] = i
        mediaMuted[row.item.id] = row.muted
      } else {
        links.push(row.item)
        linkDurations[row.item.id] = defaultDurationFor(row)
        linkOrder[row.item.id] = i
      }
    })
    const total = Object.values(mediaDurations).reduce((a, b) => a + b, 0)
      + Object.values(linkDurations).reduce((a, b) => a + b, 0)
    setPlaylist({
      ...playlist,
      media, links,
      media_durations: mediaDurations,
      link_durations: linkDurations,
      media_order: mediaOrder,
      link_order: linkOrder,
      media_muted: mediaMuted,
      total_duration: total,
    })
  }

  // The picker runs in `removable` mode, so `picked` is the FULL desired set
  // of content (retained + added; deselected items are absent). We diff it
  // against the current rows: keep existing rows still present (preserving
  // their duration / mute / order), append genuinely-new picks at the end,
  // and drop anything no longer selected. One PATCH via persistRows.
  const handleSetContent = async (picked: PickedItem[]) => {
    if (!playlist) return
    setPickerOpen(false)
    const pickedMedia = new Set(picked.filter(p => p.kind === 'media').map(p => p.id))
    const pickedLinks = new Set(picked.filter(p => p.kind === 'link').map(p => p.id))
    const existingRowKeys = new Set(rows.map(r => r.key))

    // 1. Retained existing rows, in their current order.
    const retained = rows.filter(r =>
      r.kind === 'media' ? pickedMedia.has(r.item.id) : pickedLinks.has(r.item.id),
    )

    // 2. Newly-picked items not already in the playlist, in picker order.
    const additions: ContentRow[] = []
    let nextPos = retained.length
    for (const p of picked) {
      if (p.kind === 'media' && !existingRowKeys.has(`m-${p.id}`)) {
        const isImage = p.file_type.startsWith('image')
        const isVideo = p.file_type.startsWith('video')
        const isPdf   = p.file_type === 'application/pdf' || /\.pdf$/i.test(p.original_name)
        additions.push({
          key: `m-${p.id}`, kind: 'media', pos: nextPos++,
          item: { id: p.id, original_name: p.original_name, file_type: p.file_type, url: p.url, duration: p.duration },
          duration: isVideo ? (p.duration ?? 0) : 10,
          isImage, isVideo, isPdf,
          muted: false,
        })
      } else if (p.kind === 'link' && !existingRowKeys.has(`l-${p.id}`)) {
        additions.push({
          key: `l-${p.id}`, kind: 'link', pos: nextPos++,
          item: { id: p.id, name: p.name, interactive_link: p.interactive_link },
          duration: 10,
        })
      }
    }

    const next = [...retained, ...additions]
    // No-op if nothing changed (same membership and order).
    if (next.length === rows.length && next.every((r, i) => r.key === rows[i].key)) return
    applyRows(next)
  }

  const handleRemoveRow = (key: string) => {
    applyRows(rows.filter(r => r.key !== key))
  }

  // Drag-to-reorder. We compute insert-before vs insert-after from the
  // pointer's y offset relative to the row's midpoint so dropping at the
  // top half pushes the dragged item *above* and the bottom half *below*.
  const handleDrop = (fromKey: string, toKey: string, dropBelow: boolean) => {
    if (fromKey === toKey) return
    const fromIdx = rows.findIndex(r => r.key === fromKey)
    let   toIdx   = rows.findIndex(r => r.key === toKey)
    if (fromIdx < 0 || toIdx < 0) return
    const moved = rows[fromIdx]
    const without = rows.filter((_, i) => i !== fromIdx)
    if (fromIdx < toIdx) toIdx -= 1
    const insertAt = dropBelow ? toIdx + 1 : toIdx
    without.splice(insertAt, 0, moved)
    applyRows(without)
  }

  const handleDurationChange = (key: string, seconds: number) => {
    const next = rows.map(r => r.key === key ? { ...r, duration: Math.max(1, seconds) } : r)
    applyRows(next)
  }

  // Per-row mute toggle for video items — buffered locally like every edit.
  const handleToggleMute = (mediaId: number) => {
    if (!playlist) return
    const next = !playlist.media_muted?.[mediaId]
    setPlaylist({
      ...playlist,
      media_muted: { ...(playlist.media_muted || {}), [mediaId]: next },
    })
  }

  // ── Inline title rename ─────────────────────────────────────────────────
  const startTitleEdit = () => {
    if (!playlist) return
    setTitleDraft(playlist.name)
    setEditingTitle(true)
    setTimeout(() => titleInputRef.current?.focus(), 0)
  }
  const commitTitleEdit = () => {
    if (!playlist) return
    const next = titleDraft.trim()
    setEditingTitle(false)
    if (!next || next === playlist.name) return
    // Buffered — persisted on Save / Publish.
    setPlaylist({ ...playlist, name: next })
  }

  const handleDuplicate = () => {
    toast.info(t('playlist.duplicateSoon', 'Duplicate is coming soon.'))
  }

  // ── Play preview ────────────────────────────────────────────────────────
  // Cycle through the currently *visible* rows (i.e. those whose schedule
  // window includes `now`). Hidden rows are skipped entirely so the
  // preview matches what a screen would actually show right now.
  const visibleRows = useMemo(
    () => rows.filter(r => isVisibleAt(getSchedule(r.key), now)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, playlist, now],
  )

  const stopPreview = () => {
    if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null }
    setPreviewing(false)
    setPreviewIdx(0)
  }

  const advanceFromIdx = (i: number) => {
    if (i + 1 >= visibleRows.length) {
      stopPreview()
      return
    }
    setPreviewIdx(i + 1)
  }

  const startPreview = () => {
    if (visibleRows.length === 0) return
    if (previewing) {
      stopPreview()
      return
    }
    setPreviewIdx(0)
    setPreviewing(true)
  }

  // Each time the previewing row changes, schedule its advance. Videos
  // self-advance via onEnded inside PreviewFrame; everything else runs on
  // a timeout sized to the row duration.
  useEffect(() => {
    if (!previewing) return
    const row = visibleRows[previewIdx]
    if (!row) { stopPreview(); return }
    if (row.kind === 'media' && row.isVideo) return  // video advances via onEnded
    const ms = Math.max(1, defaultDurationFor(row)) * 1000
    timerRef.current = window.setTimeout(() => advanceFromIdx(previewIdx), ms)
    return () => {
      if (timerRef.current) { window.clearTimeout(timerRef.current); timerRef.current = null }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewing, previewIdx, visibleRows.length])

  // ── Info bar ────────────────────────────────────────────────────────────
  const infoBar = useMemo(() => {
    if (!playlist) return null
    // Header tile mirrors the Content Hub card — first renderable image
    // (or cached video frame) over a Clapperboard fallback.
    const headerThumb = playlistThumbUrl(playlist.media)
    return (
      <div className="flex items-center justify-between gap-3 w-full ps-2 pe-3">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => guardNavigate(() => navigate(-1))}
            aria-label={t('common.back', 'Back')}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <ChevronIcon className="w-4 h-4" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0 overflow-hidden">
            {headerThumb ? (
              <img src={headerThumb} alt={playlist.name} className="w-full h-full object-cover" />
            ) : (
              <Clapperboard className="w-5 h-5 text-primary" />
            )}
          </div>
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 min-w-0">
              {editingTitle ? (
                <input
                  ref={titleInputRef}
                  value={titleDraft}
                  onChange={e => setTitleDraft(e.target.value)}
                  onBlur={commitTitleEdit}
                  onKeyDown={e => {
                    if (e.key === 'Enter') commitTitleEdit()
                    if (e.key === 'Escape') { setEditingTitle(false); setTitleDraft(playlist.name) }
                  }}
                  maxLength={150}
                  className="text-[15px] font-semibold leading-tight bg-transparent border-b border-primary outline-none min-w-0 w-48"
                />
              ) : (
                <>
                  <h1 className="text-[15px] font-semibold text-foreground leading-tight truncate">
                    {playlist.name}
                  </h1>
                  <button
                    type="button"
                    onClick={startTitleEdit}
                    aria-label={t('common.rename', 'Rename')}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground leading-tight truncate">
              {t('playlist.addedOn', 'Added on')} {formatDate(playlist.created_at, i18n.language)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Save appears only while there are buffered, unsaved edits. */}
          {dirty && (
            <Button onClick={saveChanges} disabled={saving} className="gap-2 h-9">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {t('common.saveChanges', 'Save changes')}
            </Button>
          )}
          <div className="flex items-stretch h-9 rounded-md overflow-hidden border border-border bg-white text-foreground">
            <button
              type="button"
              onClick={() => setPublishMode('publish')}
              className="flex items-center gap-2 px-3 text-sm font-semibold hover:bg-muted transition-colors"
            >
              <Upload className="w-4 h-4" />
              {t('playlist.publish', 'Publish')}
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger
                aria-label={t('playlist.publishOptions', 'Publish options')}
                className="px-2 border-s border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex items-center"
              >
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={() => setPublishMode('schedule')} className="gap-2">
                  <CalendarClock className="w-4 h-4" />
                  {t('playlist.schedule', 'Schedule')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger
              aria-label={t('common.more', 'More')}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-border bg-muted/40 text-foreground hover:bg-muted transition-colors"
            >
              <MoreHorizontal className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={handleDuplicate} className="gap-2">
                <Copy className="w-4 h-4" />
                {t('common.duplicate', 'Duplicate')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="gap-2 text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4" />
                {t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    )
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist, editingTitle, titleDraft, i18n.language, dirty, saving])

  usePageHeader(infoBar ? { render: infoBar } : { title: t('nav.media', 'Media Library') })

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!playlist) return null

  const previewRow = previewing ? visibleRows[previewIdx] : (visibleRows[0] ?? null)

  return (
    <div className="-m-4 sm:-m-[var(--space-l)] p-4 sm:p-[var(--space-l)] h-[calc(100dvh-3.5rem-1px)] flex flex-col bg-background">
      {/* Two distinct cards on the canvas — the content editor and the preview
          each read as their own bordered, rounded card with a gutter between
          them (mirrors the screen page's Content | Preview split). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">

        {/* ── Left column — Content ─────────────────────────────────
            (Playlists no longer carry a background-audio layer — audio is its
            own channel now, published separately from the Audio Hub.) */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="mb-4 inline-flex self-start items-center gap-1.5 text-sm font-medium text-foreground">
            <Clapperboard className="w-4 h-4" />
            {t('playlist.tabContent', 'Content')}
            {rows.length > 0 && (
              <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{rows.length}</Badge>
            )}
          </div>

          <div className="flex-1 overflow-y-auto -mx-2 px-2">
            {rows.length === 0 ? (
              <EmptyContentState />
            ) : (
              <ul className="space-y-2">
                  {rows.map((row, idx) => {
                    const sched   = getSchedule(row.key)
                    const visible = isVisibleAt(sched, now)
                    const activeVisibleIdx = previewing ? visibleRows.findIndex(r => r.key === previewRow?.key) : -1
                    const isActive = previewing && visibleRows[activeVisibleIdx]?.key === row.key
                    return (
                      <ContentRowView
                        key={row.key}
                        row={row}
                        index={idx}
                        isActive={isActive}
                        thumbCache={thumbCache}
                        dragKey={dragKey}
                        dragOverKey={dragOverKey}
                        schedule={sched}
                        visible={visible}
                        onDragStart={() => setDragKey(row.key)}
                        onDragOver={(e, dropBelow) => {
                          e.preventDefault()
                          setDragOverKey(row.key + (dropBelow ? ':below' : ':above'))
                        }}
                        onDragLeave={() => setDragOverKey(prev => prev?.startsWith(row.key) ? null : prev)}
                        onDrop={(dropBelow) => {
                          if (dragKey) handleDrop(dragKey, row.key, dropBelow)
                          setDragKey(null); setDragOverKey(null)
                        }}
                        onDragEnd={() => { setDragKey(null); setDragOverKey(null) }}
                        onRemove={() => handleRemoveRow(row.key)}
                        onDurationChange={(s) => handleDurationChange(row.key, s)}
                        onScheduleClick={() => setScheduleRowKey(row.key)}
                        onToggleMute={
                          row.kind === 'media' && row.isVideo
                            ? () => handleToggleMute(row.item.id)
                            : undefined
                        }
                      />
                    )
                  })}
                </ul>
              )}
          </div>

          <div className="pt-4 mt-4 border-t border-border flex justify-center">
            <Button
              onClick={() => setPickerOpen(true)}
              className="gap-2 h-11 px-6 bg-white text-foreground hover:bg-muted border border-border"
            >
              <Plus className="w-4 h-4" />
              {t('playlist.addContent', 'Add Content')}
            </Button>
          </div>
        </div>

        {/* ── Right column — Preview ──────────────────────────────── */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="flex-1 flex items-center justify-center min-h-0">
            <PreviewFrame
              orientation={orientation}
              row={previewRow}
              previewing={previewing}
              thumbCache={thumbCache}
              onVideoEnded={() => advanceFromIdx(previewIdx)}
              progressKey={previewIdx}
              progressMs={previewRow ? Math.max(1, defaultDurationFor(previewRow)) * 1000 : 0}
              currentIndex={previewIdx}
              totalItems={visibleRows.length}
            />
          </div>

          <div className="pt-4 mt-4 flex items-center justify-between">
            <Button
              variant="ghost"
              disabled={visibleRows.length === 0}
              onClick={startPreview}
              className="gap-2 h-10 px-4 bg-muted/40 text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              {previewing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {previewing ? t('playlist.stopPreview', 'Stop Preview') : t('playlist.playPreview', 'Play Preview')}
            </Button>
            <div className="flex items-center gap-1">
              <OrientationButton
                active={orientation === 'landscape'}
                onClick={() => setOrientation('landscape')}
                icon={RectangleHorizontal}
                label={t('playlist.landscape', 'Landscape')}
              />
              <OrientationButton
                active={orientation === 'portrait'}
                onClick={() => setOrientation('portrait')}
                icon={RectangleVertical}
                label={t('playlist.portrait', 'Portrait')}
              />
            </div>
          </div>
        </div>
      </div>

      <PlaylistContentPicker
        open={pickerOpen}
        removable
        excludeIds={playlist.media.map(m => m.id)}
        excludeLinkIds={playlist.links.map(l => l.id)}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleSetContent}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t('common.deletePlaylist', 'Delete Playlist')}
        description={t('common.deletePlaylistDesc', 'This playlist will be removed. Media files are not deleted.')}
        warning={
          playlist.screens.length > 0
            ? t(
                'common.deletePlaylistDescPublished',
                "Showing on these screens — they'll go blank until you publish something else:",
              )
            : undefined
        }
        warningItems={
          playlist.screens.length > 0
            ? playlist.screens.map(s =>
                `${s.name} — ${s.branch?.name ?? t('common.deletePlaylistScreenNoBranch', 'No branch')}`,
              )
            : undefined
        }
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />

      <PublishDialog<PlaylistData>
        open={publishMode !== null}
        mode={publishMode ?? 'publish'}
        kind="playlist"
        // Flush buffered edits first so screens receive the just-edited playlist,
        // then publish. This is the "publish commits the changes" path.
        publish={withCompanions(async payload => {
          await saveChanges()
          return publishPlaylist(playlist.id, payload)
        })}
        onClose={() => setPublishMode(null)}
        onPublished={(updated) => { setPlaylist(updated); setSavedSig(playlistSig(updated)); setPublishMode(null) }}
      />

      <RowScheduleDialog
        open={scheduleRowKey !== null}
        rowLabel={(() => {
          const r = rows.find(x => x.key === scheduleRowKey)
          return r ? (r.kind === 'media' ? r.item.original_name : r.item.name) : ''
        })()}
        value={scheduleRowKey ? getSchedule(scheduleRowKey) : DEFAULT_SCHEDULE}
        onClose={() => setScheduleRowKey(null)}
        onSave={(next) => {
          if (scheduleRowKey) updateSchedule(scheduleRowKey, next)
          setScheduleRowKey(null)
        }}
      />

      {/* Unsaved-changes guard — fires when leaving with buffered edits. */}
      <ConfirmDialog
        open={guardOpen}
        title={t('common.unsavedTitle', 'Leave without publishing?')}
        description={t('common.unsavedDesc', "You didn't publish your changes. Your changes will be lost if you leave.")}
        confirmLabel={t('common.leaveDiscard', 'Leave & discard')}
        onConfirm={confirmLeave}
        onClose={cancelLeave}
      />
    </div>
  )
}

// PublishDialog and RowScheduleDialog have been extracted to
// apps/frame/src/components/ so LayoutDetailsPage can reuse them and
// keep behaviour in lock-step with playlists.

// ── ContentRowView ───────────────────────────────────────────────────────
// One row in the playlist's content list. Handles its own drag-target
// hit-zones (top half = insert-above, bottom half = insert-below) and
// renders an editable duration input for non-video items.
function ContentRowView({
  row, index, isActive, thumbCache,
  dragKey, dragOverKey,
  schedule, visible,
  onDragStart, onDragOver, onDragLeave, onDrop, onDragEnd,
  onRemove, onDurationChange, onScheduleClick, onToggleMute,
}: {
  row: ContentRow
  index: number
  isActive: boolean
  thumbCache: Record<number, string>
  dragKey: string | null
  dragOverKey: string | null
  schedule: RowSchedule
  visible: boolean
  onDragStart: () => void
  onDragOver: (e: React.DragEvent, dropBelow: boolean) => void
  onDragLeave: () => void
  onDrop: (dropBelow: boolean) => void
  onDragEnd: () => void
  onRemove: () => void
  onDurationChange: (seconds: number) => void
  onScheduleClick: () => void
  onToggleMute?: () => void
}) {
  const { t } = useTranslation()
  const isVideo = row.kind === 'media' && row.isVideo
  const isImage = row.kind === 'media' && row.isImage
  const isPdf   = row.kind === 'media' && row.isPdf
  const isLink  = row.kind === 'link'

  const url = row.kind === 'media'
    ? (row.item.url.startsWith('http') ? row.item.url : `${BACKEND_URL}${row.item.url}`)
    : ''
  const thumb = row.kind === 'media' ? thumbCache[row.item.id] : undefined

  const isOverAbove = dragOverKey === `${row.key}:above` && dragKey && dragKey !== row.key
  const isOverBelow = dragOverKey === `${row.key}:below` && dragKey && dragKey !== row.key

  return (
    <li
      draggable
      onDragStart={onDragStart}
      onDragOver={e => {
        const rect = (e.currentTarget as HTMLLIElement).getBoundingClientRect()
        const dropBelow = e.clientY > rect.top + rect.height / 2
        onDragOver(e, dropBelow)
      }}
      onDragLeave={onDragLeave}
      onDrop={e => {
        e.preventDefault()
        const rect = (e.currentTarget as HTMLLIElement).getBoundingClientRect()
        const dropBelow = e.clientY > rect.top + rect.height / 2
        onDrop(dropBelow)
      }}
      onDragEnd={onDragEnd}
      className={`group relative flex items-center gap-3 p-3 rounded-xl border bg-muted/40 transition-colors cursor-grab active:cursor-grabbing ${
        isActive ? 'border-primary ring-2 ring-primary/30' : 'border-border/60 hover:bg-muted/60'
      } ${dragKey === row.key ? 'opacity-50' : ''} ${!visible ? 'opacity-60' : ''}`}
    >
      {isOverAbove && <span className="absolute -top-1 inset-x-0 h-0.5 bg-primary rounded-full" />}
      {isOverBelow && <span className="absolute -bottom-1 inset-x-0 h-0.5 bg-primary rounded-full" />}

      <GripVertical className="w-4 h-4 text-muted-foreground/60 shrink-0" />
      <span className="text-xs text-muted-foreground font-mono w-5 text-center shrink-0">{index + 1}</span>

      <div className="w-16 h-10 rounded-lg overflow-hidden bg-muted shrink-0 border border-border/40">
        {isImage && <img src={url} alt={row.kind === 'media' ? row.item.original_name : ''} className="w-full h-full object-cover" loading="lazy" />}
        {isVideo && thumb && <img src={thumb} alt="" className="w-full h-full object-cover" />}
        {isVideo && !thumb && (
          <div className="w-full h-full flex items-center justify-center bg-purple-500/10">
            <Video className="w-4 h-4 text-purple-400" />
          </div>
        )}
        {isPdf && (
          <PdfThumbnail url={url} targetWidth={128} fit="cover" className="w-full h-full" />
        )}
        {isLink && (
          <div className="w-full h-full flex items-center justify-center bg-teal-500/10">
            <Link2 className="w-4 h-4 text-teal-600" />
          </div>
        )}
        {row.kind === 'media' && !isImage && !isVideo && !isPdf && (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <FileType className="w-4 h-4 text-muted-foreground" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">
          {row.kind === 'media' ? row.item.original_name : row.item.name}
        </p>
        <div className="flex items-center gap-2 mt-0.5">
          {isImage && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-0">
              <ImageIcon className="w-2 h-2" /> {t('common.image', 'Image')}
            </Badge>
          )}
          {isVideo && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-0">
              <Video className="w-2 h-2" /> {t('common.video', 'Video')}
            </Badge>
          )}
          {isPdf && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-0">
              <FileText className="w-2 h-2" /> {t('common.document', 'Document')}
            </Badge>
          )}
          {isLink && (
            <Badge variant="secondary" className="text-[9px] gap-0.5 py-0 h-4 bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 border-0">
              <Link2 className="w-2 h-2" /> {t('common.link', 'Webpage')}
            </Badge>
          )}
          {!schedule.alwaysOn && (
            <Badge
              variant="secondary"
              className={`text-[9px] gap-0.5 py-0 h-4 border-0 ${
                visible
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <CalendarClock className="w-2 h-2" />
              {visible
                ? t('playlist.rowScheduled', 'Scheduled')
                : t('playlist.rowHidden', 'Hidden')}
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
        <Clock className="w-3 h-3" />
        {isVideo ? (
          <span className="font-medium">
            {fmtDur(defaultDurationFor(row))} <span className="text-[10px] opacity-70">({t('playlist.auto', 'Auto')})</span>
          </span>
        ) : (
          <DurationInput
            value={defaultDurationFor(row)}
            onCommit={onDurationChange}
            ariaLabel={t('playlist.rowDurationAria', 'Duration in seconds')}
          />
        )}
      </div>

      {onToggleMute && row.kind === 'media' && (
        <button
          type="button"
          onClick={e => { e.stopPropagation(); onToggleMute() }}
          onMouseDown={e => e.stopPropagation()}
          draggable={false}
          aria-label={row.muted
            ? t('playlist.rowUnmute', 'Unmute video')
            : t('playlist.rowMute', 'Mute video')}
          title={row.muted
            ? t('playlist.rowUnmute', 'Unmute video')
            : t('playlist.rowMute', 'Mute video')}
          className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors shrink-0 ${
            row.muted
              ? 'text-amber-600 opacity-100 hover:bg-amber-500/10'
              : 'text-muted-foreground opacity-0 group-hover:opacity-100 touch-reveal hover:text-foreground hover:bg-muted'
          }`}
        >
          {row.muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
      )}

      <button
        type="button"
        onClick={e => { e.stopPropagation(); onScheduleClick() }}
        onMouseDown={e => e.stopPropagation()}
        draggable={false}
        aria-label={t('playlist.rowScheduleAria', 'Schedule this item')}
        className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors shrink-0 ${
          !schedule.alwaysOn
            ? 'text-primary opacity-100'
            : 'text-muted-foreground opacity-0 group-hover:opacity-100 touch-reveal hover:text-foreground hover:bg-muted'
        }`}
      >
        <CalendarClock className="w-4 h-4" />
      </button>

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('common.remove', 'Remove')}
        className="w-7 h-7 max-md:w-10 max-md:h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 opacity-0 group-hover:opacity-100 touch-reveal"
      >
        <X className="w-4 h-4" />
      </button>
    </li>
  )
}

// ── EmptyContentState ────────────────────────────────────────────────────
function EmptyContentState() {
  const { t } = useTranslation()
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6">
      <div className="relative w-44 h-44 mb-6 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-muted-foreground/15" />
        <svg viewBox="0 0 96 72" className="relative w-28 h-20 text-white fill-current drop-shadow">
          <rect x="0"  y="0"  width="96" height="72" rx="10" />
          <rect x="14" y="14" width="14" height="14" rx="3" className="text-muted-foreground/30 fill-current" />
          <rect x="34" y="14" width="14" height="14" rx="3" className="text-muted-foreground/30 fill-current" />
          <rect x="54" y="14" width="14" height="14" rx="3" className="text-muted-foreground/30 fill-current" />
          <rect x="14" y="36" width="56" height="4"  rx="2" className="text-muted-foreground/30 fill-current" />
          <rect x="14" y="46" width="50" height="4"  rx="2" className="text-muted-foreground/30 fill-current" />
          <rect x="14" y="56" width="40" height="4"  rx="2" className="text-muted-foreground/30 fill-current" />
        </svg>
      </div>
      <h3 className="text-base font-semibold text-foreground mb-2">
        {t('playlist.emptyTitle', 'This Playlist is nothing without content')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm leading-relaxed">
        {t('playlist.emptyDescription', 'Add your media, documents or links and schedule them for something great.')}
      </p>
    </div>
  )
}

// ── PreviewFrame ─────────────────────────────────────────────────────────
// Renders the currently-active row inside a 16:9 (landscape) or 9:16
// (portrait) frame. Images / PDFs / links show as static thumbs; videos
// auto-play and call onVideoEnded so the parent can advance. When
// previewing, a thin progress bar at the bottom of the frame animates
// from 0→100% over the current item's duration so the preview looks like
// a real video player tracking through a sequence.
function PreviewFrame({
  orientation, row, previewing, thumbCache, onVideoEnded,
  progressKey, progressMs, currentIndex, totalItems,
}: {
  orientation: 'landscape' | 'portrait'
  row: ContentRow | null
  previewing: boolean
  thumbCache: Record<number, string>
  onVideoEnded: () => void
  progressKey: number
  progressMs: number
  currentIndex: number
  totalItems: number
}) {
  const { t } = useTranslation()
  const aspect = orientation === 'landscape' ? 'aspect-video' : 'aspect-[9/16]'
  const widthClass = orientation === 'landscape' ? 'w-full max-w-xl' : 'h-full max-h-[60vh] aspect-[9/16]'
  const frameClass = `relative ${orientation === 'landscape' ? widthClass : ''} ${orientation === 'portrait' ? widthClass : aspect} rounded-xl border-2 border-border bg-muted/30 overflow-hidden flex items-center justify-center`

  // Keyframe defined once and reused via `animation-name`. We re-mount the
  // bar with `key={progressKey}` whenever the item changes so the
  // animation restarts cleanly. Videos share the same animation so the
  // bar tracks playback even though we don't read the <video> currentTime.
  const keyframes = `@keyframes meno-preview-progress { from { width: 0% } to { width: 100% } }`

  // Overlay shown over every body type when previewing so the bar + the
  // "X / Y" badge live inside the frame like a YouTube-style overlay.
  const overlay = previewing && totalItems > 0 && (
    <>
      <style>{keyframes}</style>
      <div className="absolute top-2 end-2 z-10 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-black/55 text-white backdrop-blur-sm">
        {currentIndex + 1} / {totalItems}
      </div>
      <div
        key={progressKey}
        className="absolute bottom-0 start-0 h-1 bg-primary"
        style={{ animation: `meno-preview-progress ${progressMs}ms linear forwards` }}
      />
    </>
  )

  if (!row) {
    return (
      <div className={frameClass}>
        <p className="text-base font-medium text-muted-foreground/80">
          {t('playlist.noPreview', 'No content to preview')}
        </p>
      </div>
    )
  }

  if (row.kind === 'link') {
    return (
      <div className={frameClass}>
        <div className="flex flex-col items-center gap-2 text-center px-6">
          <Link2 className="w-14 h-14 text-teal-600" />
          <p className="text-sm font-medium text-foreground">{row.item.name}</p>
          <p className="text-[11px] text-muted-foreground break-all max-w-xs">{row.item.interactive_link}</p>
        </div>
        {overlay}
      </div>
    )
  }

  const m = row.item
  const isImage = row.isImage
  const isVideo = row.isVideo
  const isPdf   = row.isPdf
  const url     = m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`
  const thumb   = thumbCache[m.id]

  return (
    <div className={frameClass}>
      {isImage && <img src={url} alt={m.original_name} className="w-full h-full object-contain" />}
      {isVideo && previewing && (
        <video
          src={url}
          autoPlay
          // Browsers block autoplay-with-sound without a user gesture; if the
          // user explicitly muted the row, also mute here so the icon matches.
          muted={row.muted}
          playsInline
          onEnded={onVideoEnded}
          className="w-full h-full object-contain"
        />
      )}
      {isVideo && !previewing && thumb && <img src={thumb} alt={m.original_name} className="w-full h-full object-contain" />}
      {isVideo && !previewing && !thumb && <Video className="w-14 h-14 text-purple-400" />}
      {isPdf && <PdfThumbnail url={url} targetWidth={480} fit="contain" className="w-full h-full" />}
      {overlay}
    </div>
  )
}

function OrientationButton({
  active, onClick, icon: Icon, label,
}: {
  active: boolean
  onClick: () => void
  icon: typeof RectangleHorizontal
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center gap-1 px-3 py-1.5 rounded-md transition-colors ${
        active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
      }`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  )
}
